import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getMessageWithPlaceholders } from 'shared-locales';
import { ContentLabel, ProgressBar, Tooltip, User } from 'components/common';
import { CompetenciesProductBadge } from 'components/competencies';
import { ProfileCompetenciesBadge } from '.';

import type { CompetencyInProduct, UserWithBasicData } from 'shared-types';
import type { MergedCompetency } from 'lib/competencies/getMergedCompetencies';

interface Props {
  numberOfMatchedCompetencies: number;
  numberOfTotalCompetencies: number;
  profileCompetencies: CompetencyInProduct[];
  user?: UserWithBasicData;
  userCompetencies: MergedCompetency[];
}

const ProfileCompetenciesCompareResult = ({
  numberOfMatchedCompetencies,
  numberOfTotalCompetencies,
  profileCompetencies,
  user,
  userCompetencies
}: Props) => {
  const { messages, theme } = useReduxState();

  const getMatchingUserCompetency = (
    targetId: number,
    targetLevelNumber: number | null
  ) => {
    const userCompetency = userCompetencies.find(
      (competency) => competency.id === targetId
    );
    if (!userCompetency) {
      return { type: 'notGrantedCompetency', targetLevelNumber };
    } else if (
      (!targetLevelNumber && !userCompetency.highestLevel?.level) ||
      userCompetency.highestLevel?.level >= targetLevelNumber
    ) {
      return { type: 'grantedCompetency', competency: userCompetency };
    } else {
      return {
        type: 'competencyWithInsufficientLevel',
        competency: userCompetency,
        targetLevelNumber
      };
    }
  };

  return (
    <>
      {user ? (
        <ContentLabel
          horizontal={true}
          horizontalBreakpoint={breakpoints.small}
          label={messages.commonUser}
        >
          <User user={user} />
        </ContentLabel>
      ) : null}

      <ContentLabel
        horizontal={true}
        horizontalBreakpoint={breakpoints.small}
        label={messages.commonProgress}
        spacing={30}
      >
        <ProgressBar
          label={getMessageWithPlaceholders({
            message: messages.competenciesNumberOfCompetencies,
            placeholders: {
              numberOfCompetencies: `${numberOfMatchedCompetencies} / ${numberOfTotalCompetencies}`
            }
          })}
          labelStyle={{ marginBottom: 3 }}
          color={theme.successColor}
          progress={numberOfMatchedCompetencies / numberOfTotalCompetencies}
          progressStyle={{ width: 200 }}
        />
      </ContentLabel>

      <ContentLabel
        horizontal={true}
        horizontalBreakpoint={breakpoints.small}
        label={messages.analyticsCompetencies}
        spacing={30}
      >
        <div className="competencies">
          {profileCompetencies.map((competency) => {
            const matchResult = getMatchingUserCompetency(
              competency.id,
              competency.level?.level
            );

            if (matchResult.type === 'notGrantedCompetency') {
              return (
                <div
                  className="competency"
                  key={`${competency.id}-${competency.level?.id || 0}`}
                >
                  <Tooltip content={messages.commonNotGranted}>
                    <CompetenciesProductBadge
                      badgeIcon="times"
                      competency={competency}
                      includeAvailableLevels={true}
                    />
                  </Tooltip>
                </div>
              );
            } else if (matchResult.type === 'grantedCompetency') {
              return (
                <div
                  className="competency"
                  key={`${competency.id}-${competency.level?.id || 0}`}
                >
                  <Tooltip content={messages.commonGranted}>
                    <ProfileCompetenciesBadge
                      badgeIcon="check"
                      competency={matchResult.competency}
                      displayLevel={competency.level}
                    />
                  </Tooltip>
                </div>
              );
            } else if (matchResult.type === 'competencyWithInsufficientLevel') {
              const info = (
                <div style={{ display: 'flex', gap: 5 }}>
                  <div>
                    {getMessageWithPlaceholders({
                      message: messages.competenciesGrantedLevel,
                      placeholders: {
                        levelNumber:
                          matchResult.competency.highestLevel?.level || 0
                      }
                    })}
                  </div>
                  <div>{'•'}</div>
                  <div>
                    {getMessageWithPlaceholders({
                      message: messages.competenciesRequiredLevel,
                      placeholders: {
                        levelNumber: matchResult.targetLevelNumber
                      }
                    })}
                  </div>
                </div>
              );

              return (
                <div
                  className="competency"
                  key={`${competency.id}-${competency.level?.id || 0}`}
                >
                  <Tooltip content={info}>
                    <ProfileCompetenciesBadge
                      badgeCategory="warning-light"
                      competency={matchResult.competency}
                      displayLevel={competency.level}
                      targetLevelNumber={matchResult.targetLevelNumber}
                    />
                  </Tooltip>
                </div>
              );
            }
          })}
        </div>
      </ContentLabel>

      <style jsx>{`
        .competencies {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
      `}</style>
    </>
  );
};

export default ProfileCompetenciesCompareResult;
